import { useQuery, type ApolloError } from "@apollo/client";
import Button from "@/components/Button";
import { SkeletonElement } from "@/components/Skeleton";
import { sendToRaygun } from "@/lib/raygunUtils";
import { FLEX_CALL_DETAILS_QUERY } from "../queries";
import useRecordingAudio from "./useRecordingAudio";

type Props = {
  callSid: string;
  hasRecording: boolean;
};

function SectionTitle({ children }: { children: string }) {
  return <h3 className="text-title-small text-sys-brand-on-surface">{children}</h3>;
}

export default function RecentCallDetails({ callSid, hasRecording }: Props) {
  const { data, loading, error, refetch } = useQuery(FLEX_CALL_DETAILS_QUERY, {
    variables: { callSid },
    onError: (queryError: ApolloError) => {
      sendToRaygun(queryError, { tags: ["call-topics", "details"] });
    }
  });
  const recording = useRecordingAudio(callSid, hasRecording && error == null);

  if (loading) {
    return (
      <div className="flex flex-col gap-2 py-2" data-testid="call-details-loading">
        <SkeletonElement height="h-4" width="w-40" rounded="rounded-full" />
        <SkeletonElement height="h-4" width="w-full" rounded="rounded-full" />
        <SkeletonElement height="h-4" width="w-3/4" rounded="rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-start gap-2 py-4" data-testid="call-topic-recent-call-error">
        <p className="text-body-medium text-sys-brand-on-surface-variant">Couldn&apos;t load these call details.</p>
        <Button variant="text" onClick={() => refetch()}>
          Try Again
        </Button>
      </div>
    );
  }

  const details = data?.twilioFlexCall;

  return (
    <div className="flex flex-col gap-6 py-4" data-testid="call-topic-recent-call-details">
      <section aria-label="Call summary" className="flex flex-col gap-2">
        <SectionTitle>Summary</SectionTitle>
        <p className="text-body-medium text-sys-brand-on-surface-variant">
          {details?.transcriptSummary || "No AI summary for this call yet."}
        </p>
      </section>

      <section aria-label="Call transcript" className="flex flex-col gap-2">
        <SectionTitle>Transcript</SectionTitle>
        {details?.transcriptText ? (
          <p
            tabIndex={0}
            className="text-body-medium text-sys-brand-on-surface-variant max-h-48 overflow-y-auto whitespace-pre-wrap"
          >
            {details.transcriptText}
          </p>
        ) : (
          <p className="text-body-medium text-sys-brand-on-surface-variant">Transcript unavailable for this call.</p>
        )}
      </section>

      <section aria-label="Call recording" className="flex flex-col gap-2">
        <SectionTitle>Recording</SectionTitle>
        {!hasRecording ? (
          <p className="text-body-medium text-sys-brand-on-surface-variant">No recording for this call.</p>
        ) : recording.loading ? (
          <SkeletonElement height="h-10" width="w-full" rounded="rounded-full" />
        ) : recording.audioUrl ? (
          <audio controls src={recording.audioUrl} className="w-full" data-testid="call-recording-player">
            Your browser does not support the audio element.
          </audio>
        ) : (
          <p className="text-body-medium text-sys-brand-on-surface-variant">
            {recording.error ?? "Recording unavailable."}
          </p>
        )}
      </section>
    </div>
  );
}
